import { motion } from "framer-motion";
import { Check, Users } from "lucide-react";
import { WHATSAPP_URL } from "../App";

const t = {
  es: {
    label: "Nuestras Tarifas",
    title: "Lujo Costero",
    titleAccent: "a Tu Alcance",
    subtitle:
      "Los precios aplican para temporada regular. Para días festivos y temporada alta, contáctanos.",
    bungalow: {
      badge: "LA EXPERIENCIA",
      name: "Bungalow Individual",
      price: "Q1,650",
      per: "/noche",
      capacity: "Capacidad: 6 personas",
      features: [
        "Hospedaje con jacuzzi privado",
        "Acceso a piscina común",
        "Cocina equipada y sala con TV",
        "Acceso directo a la playa",
        "Parqueo seguro incluido",
        "Descuento al hospedarte 3+ noches",
      ],
      cta: "Reservar Bungalow",
    },
    complex: {
      badge: "GRAN RETIRO",
      exclusive: "MÁS EXCLUSIVO",
      name: "Complejo Privado",
      price: "Q2,500",
      per: "/noche",
      capacity: "Capacidad: 15–20 personas",
      features: [
        "Exclusividad total del complejo",
        "Piscina privada",
        "6 habitaciones con baño privado",
        "Cocina equipada y equipo de sonido",
        "Área de churrasquera privada",
        "Parqueo privado para 6+ vehículos",
      ],
      cta: "Reservar Complejo",
    },
  },
  en: {
    label: "Our Rates",
    title: "Coastal Luxury",
    titleAccent: "Within Reach",
    subtitle:
      "Prices apply for regular season. For holidays and high season, please contact us.",
    bungalow: {
      badge: "THE EXPERIENCE",
      name: "Individual Bungalow",
      price: "Q1,650",
      per: "/night",
      capacity: "Capacity: 6 people",
      features: [
        "Accommodation with private jacuzzi",
        "Shared main pool access",
        "Equipped kitchen and TV lounge",
        "Direct beach access",
        "Secure on-site parking",
        "Discount for 3+ night stays",
      ],
      cta: "Choose Bungalow",
    },
    complex: {
      badge: "GRAND RETREAT",
      exclusive: "MOST EXCLUSIVE",
      name: "Full Private Complex",
      price: "Q2,500",
      per: "/night",
      capacity: "Capacity: 15–20 people",
      features: [
        "Entire property exclusivity",
        "Private swimming pool",
        "6 rooms with private bathrooms",
        "Kitchen and sound system",
        "Private BBQ area",
        "Parking for up to 6 vehicles",
      ],
      cta: "Book Full Complex",
    },
  },
};

export default function Pricing({ lang }) {
  const tr = t[lang];
  const waBungalow = WHATSAPP_URL + "&text2=bungalow individual.";
  const waComplex = WHATSAPP_URL + "&text2=complejo privado.";

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-28 bg-[#FAFAF9]"
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="max-w-2xl mx-auto text-center mb-16">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="font-montserrat text-[#8C5E35] text-sm font-semibold tracking-widest uppercase mb-4"
          >
            {tr.label}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-montserrat font-extrabold text-[#1F3A5E] text-4xl md:text-5xl leading-tight mb-4"
            data-testid="pricing-title"
          >
            {tr.title}{" "}
            <span className="text-[#8C5E35]">{tr.titleAccent}</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="font-dm-sans text-gray-500 text-base"
          >
            {tr.subtitle}
          </motion.p>
        </div>

        {/* Cards */}
        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Bungalow Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            data-testid="pricing-bungalow"
            className="bg-white rounded-3xl overflow-hidden border border-[#D4B895]/40 shadow-xl shadow-[#1F3A5E]/5 flex flex-col"
          >
            {/* Image */}
            <div className="relative h-52 overflow-hidden">
              <img
                src="/images/galeria/B1.jpg"
                alt="Individual Bungalow"
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              <div className="absolute bottom-4 left-5">
                <span className="font-montserrat text-white/80 text-xs font-semibold tracking-widest uppercase">
                  {tr.bungalow.badge}
                </span>
                <h3 className="font-montserrat font-extrabold text-white text-2xl">
                  {tr.bungalow.name}
                </h3>
              </div>
            </div>

            {/* Body */}
            <div className="p-8 flex flex-col flex-1">
              <div className="flex items-baseline gap-1 mb-2">
                <span className="font-montserrat font-extrabold text-[#1F3A5E] text-4xl">
                  {tr.bungalow.price}
                </span>
                <span className="font-dm-sans text-gray-400 text-sm">
                  {tr.bungalow.per}
                </span>
              </div>
              <div className="flex items-center gap-2 text-gray-400 text-sm font-dm-sans mb-6 pb-6 border-b border-[#1F3A5E]/8">
                <Users size={14} />
                {tr.bungalow.capacity}
              </div>
              <ul className="space-y-3 flex-1 mb-8">
                {tr.bungalow.features.map((feat, i) => (
                  <li key={i} className="flex items-start gap-3 font-dm-sans text-gray-600 text-sm">
                    <div className="w-5 h-5 rounded-full bg-[#1F3A5E]/8 flex items-center justify-center shrink-0 mt-0.5">
                      <Check size={11} className="text-[#1F3A5E]" />
                    </div>
                    {feat}
                  </li>
                ))}
              </ul>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pricing-bungalow-cta"
                className="block text-center py-3.5 rounded-full border-2 border-[#1F3A5E] text-[#1F3A5E] font-montserrat font-bold text-sm hover:bg-[#1F3A5E] hover:text-white transition-all duration-200"
              >
                {tr.bungalow.cta}
              </a>
            </div>
          </motion.div>

          {/* Complex Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            data-testid="pricing-complex"
            className="bg-[#1F3A5E] rounded-3xl overflow-hidden shadow-2xl shadow-[#1F3A5E]/30 flex flex-col"
          >
            {/* Image */}
            <div className="relative h-52 overflow-hidden">
              <img
                src="/images/galeria/P1.jpg"
                alt="Complejo Privado"
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1F3A5E]/80 to-transparent" />
              {/* Badge */}
              <div className="absolute top-4 right-4 bg-[#8C5E35] text-white text-xs font-montserrat font-bold px-3 py-1 rounded-full tracking-wider">
                {tr.complex.exclusive}
              </div>
              <div className="absolute bottom-4 left-5">
                <span className="font-montserrat text-white/70 text-xs font-semibold tracking-widest uppercase">
                  {tr.complex.badge}
                </span>
                <h3 className="font-montserrat font-extrabold text-white text-2xl">
                  {tr.complex.name}
                </h3>
              </div>
            </div>

            {/* Body */}
            <div className="p-8 flex flex-col flex-1">
              <div className="flex items-baseline gap-1 mb-2">
                <span className="font-montserrat font-extrabold text-white text-4xl">
                  {tr.complex.price}
                </span>
                <span className="font-dm-sans text-white/50 text-sm">
                  {tr.complex.per}
                </span>
              </div>
              <div className="flex items-center gap-2 text-white/50 text-sm font-dm-sans mb-6 pb-6 border-b border-white/10">
                <Users size={14} />
                {tr.complex.capacity}
              </div>
              <ul className="space-y-3 flex-1 mb-8">
                {tr.complex.features.map((feat, i) => (
                  <li key={i} className="flex items-start gap-3 font-dm-sans text-white/80 text-sm">
                    <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center shrink-0 mt-0.5">
                      <Check size={11} className="text-[#D4B895]" />
                    </div>
                    {feat}
                  </li>
                ))}
              </ul>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pricing-complex-cta"
                className="block text-center py-3.5 rounded-full bg-white text-[#1F3A5E] font-montserrat font-bold text-sm hover:bg-[#D4B895] transition-colors duration-200"
              >
                {tr.complex.cta}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
